import { useEffect, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { api, API } from "@/lib/api";
import { Reveal, Overline } from "./Reveal";

export const fileUrl = (path) => (path ? `${API}/files/${path}` : "");
export const pick = (obj, lang) => obj?.[lang] || obj?.it || "";

const ServiceRow = ({ s, i, lang }) => (
  <Reveal delay={0.05} className="group">
    <div data-testid={`service-item-${s.service_id}`} className={`grid gap-6 md:gap-12 items-center md:grid-cols-12 py-10 md:py-14 border-t border-white/10`}>
      <div className={`md:col-span-5 ${i % 2 ? "md:order-2" : ""}`}>
        <div className="relative aspect-[4/3] overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/[0.03]">
          {s.image ? (
            <img src={fileUrl(s.image)} alt={pick(s.title, lang)} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
          ) : (
            <div className="absolute inset-0 bento-hero" />
          )}
          <span className="absolute top-4 left-4 font-mono text-xs text-white/80 bg-[#050713]/60 backdrop-blur px-2.5 py-1 rounded-full">0{i + 1}</span>
        </div>
      </div>
      <div className={`md:col-span-7 ${i % 2 ? "md:order-1 md:pr-6" : "md:pl-6"}`}>
        <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.02]">{pick(s.title, lang)}</h3>
        <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl whitespace-pre-line">{pick(s.desc, lang)}</p>
      </div>
    </div>
  </Reveal>
);

const Clients = ({ items, t }) => (
  <div className="mt-28 md:mt-36" data-testid="clients-block">
    <Reveal><Overline>{t.clientsOver}</Overline></Reveal>
    <Reveal delay={0.08}><h3 className="mt-5 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-white">{t.clientsTitle}</h3></Reveal>
    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {items.map((c, i) => {
        const Tag = c.url ? "a" : "div";
        const props = c.url ? { href: c.url, target: "_blank", rel: "noreferrer" } : {};
        return (
          <Reveal key={c.client_id} delay={(i % 5) * 0.05}>
            <Tag {...props} data-testid={`client-item-${c.client_id}`} className="bento-card group flex flex-col items-center justify-center gap-3 rounded-2xl p-6 min-h-[132px] text-center">
              {c.logo ? (
                <img src={fileUrl(c.logo)} alt={c.name} loading="lazy" className="max-h-14 max-w-[80%] object-contain opacity-80 grayscale transition-[opacity,filter] duration-500 group-hover:opacity-100 group-hover:grayscale-0" />
              ) : (
                <span className="font-display text-2xl font-extrabold text-chrome">{c.name}</span>
              )}
              <span className="text-xs text-slate-400 flex items-center gap-1">{c.name}{c.url && <ArrowUpRight className="w-3 h-3" />}</span>
            </Tag>
          </Reveal>
        );
      })}
    </div>
  </div>
);

const Portfolio = ({ items, t }) => {
  const [active, setActive] = useState(null);
  return (
    <div className="mt-28 md:mt-36" data-testid="portfolio-block">
      <Reveal><Overline>{t.portfolioOver}</Overline></Reveal>
      <Reveal delay={0.08}><h3 className="mt-5 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-white">{t.portfolioTitle}</h3></Reveal>
      <div className="mt-10 columns-2 md:columns-3 lg:columns-4 gap-3">
        {items.map((p, i) => (
          <Reveal key={p.item_id} delay={(i % 4) * 0.05} className="mb-3 break-inside-avoid">
            <button onClick={() => setActive(p)} data-testid={`portfolio-item-${p.item_id}`} className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] text-left">
              <img src={fileUrl(p.image)} alt={p.caption} loading="lazy" className="w-full h-auto object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]" />
              {p.caption && <span className="absolute inset-x-0 bottom-0 p-4 text-sm text-white bg-gradient-to-t from-[#050713]/90 to-transparent opacity-0 translate-y-2 transition-[opacity,transform] duration-500 group-hover:opacity-100 group-hover:translate-y-0">{p.caption}</span>}
            </button>
          </Reveal>
        ))}
      </div>
      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)} data-testid="portfolio-lightbox" className="fixed inset-0 z-[100] bg-[#050713]/92 backdrop-blur-xl grid place-items-center p-5 cursor-zoom-out">
            <button aria-label={t.close} data-testid="portfolio-lightbox-close" className="absolute top-5 right-5 p-2 text-white/80 hover:text-white"><X className="w-6 h-6" /></button>
            <motion.img initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} src={fileUrl(active.image)} alt={active.caption} className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl" />
            {active.caption && <p className="absolute bottom-6 inset-x-0 text-center text-sm text-slate-200">{active.caption}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Services = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState({ services: [], clients: [], portfolio: [] });

  useEffect(() => {
    api.get("/showcase").then(({ data: d }) => setData({ services: d?.services ?? [], clients: d?.clients ?? [], portfolio: d?.portfolio ?? [] })).catch(() => {});
  }, []);

  const s = t.services;
  if (!data.services.length && !data.clients.length && !data.portfolio.length) return null;

  return (
    <section id="services" className="relative py-28 md:py-40" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        {data.services.length > 0 && (
          <>
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-8">
                <Reveal><Overline>{s.over}</Overline></Reveal>
                <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-white">{s.title}</h2></Reveal>
              </div>
              <Reveal delay={0.14} className="lg:col-span-4"><p className="text-slate-300 leading-relaxed">{s.sub}</p></Reveal>
            </div>
            <div className="mt-14 border-b border-white/10">
              {data.services.map((sv, i) => <ServiceRow key={sv.service_id} s={sv} i={i} lang={lang} />)}
            </div>
          </>
        )}
        {data.clients.length > 0 && <Clients items={data.clients} t={s} />}
        {data.portfolio.length > 0 && <Portfolio items={data.portfolio} t={s} />}
      </div>
    </section>
  );
};
